import SimpleStorage from '../utils/simpleStorage';
import { Platform } from 'react-native';

export interface FavoriteBusiness {
  id: string;
  ownerId?: string;
  businessId?: string;
  firestoreId?: string;
  docId?: string;
  name: string;
  category: string;
  rating: number;
  distance: string;
  address: string;
  phone: string;
  imageUrl?: string;
  services: string[];
  priceRange: string;
  hours: string;
  addedDate: string;
  /** If true, business is on BookAPT (Book opens select services). If false/undefined, Book opens Request Appointment form. */
  isOnBookAPT?: boolean;
}

class FavoritesService {
  private readonly FAVORITES_KEY = 'bookapt_user_favorites'; // Use bookapt_ prefix for SimpleStorage compatibility

  async getFavorites(): Promise<FavoriteBusiness[]> {
    try {
      // LOCAL-FIRST STRATEGY: Load from local storage first (has the latest data)
      let localFavorites: FavoriteBusiness[] = [];
      let localUpdatedAt: string | null = null;
      
      // Try localStorage first (fastest)
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
        try {
          const stored = window.localStorage.getItem(this.FAVORITES_KEY);
          if (stored) {
            localFavorites = JSON.parse(stored);
            if (Array.isArray(localFavorites) && localFavorites.length > 0) {
              // Get the most recent addedDate as a proxy for "last updated"
              const dates = localFavorites.map(f => f.addedDate).filter(Boolean).sort().reverse();
              localUpdatedAt = dates[0] || null;
              console.log(`✅ FavoritesService: Loaded ${localFavorites.length} favorites from localStorage`);
            }
          }
        } catch (localStorageError) {
          console.warn('⚠️ FavoritesService: Error reading from localStorage:', localStorageError);
        }
      }
      
      // Fallback to SimpleStorage if localStorage didn't have data
      if (localFavorites.length === 0) {
        try {
          const favorites = await SimpleStorage.getItem(this.FAVORITES_KEY);
          if (favorites) {
            localFavorites = JSON.parse(favorites);
            if (Array.isArray(localFavorites) && localFavorites.length > 0) {
              const dates = localFavorites.map(f => f.addedDate).filter(Boolean).sort().reverse();
              localUpdatedAt = dates[0] || null;
              console.log(`✅ FavoritesService: Loaded ${localFavorites.length} favorites from SimpleStorage`);
            }
          }
        } catch (simpleStorageError) {
          console.warn('⚠️ FavoritesService: Error reading from SimpleStorage:', simpleStorageError);
        }
      }
      
      // Now try to sync from Firestore (for cross-device sync)
      let firestoreFavorites: FavoriteBusiness[] = [];
      let firestoreUpdatedAt: string | null = null;

      try {
        const { UnifiedAuthService, initializeFirebase } = require('./unifiedAuthService');
        const initialized = initializeFirebase();
        if (initialized) {
          const user = await UnifiedAuthService.getCurrentUser();
          let userId = user?.uid || user?.id;
          // Mobile fallback: UnifiedAuthService may return Firestore doc data without id field
          if (!userId && Platform.OS !== 'web') {
            try {
              const authModule = require('@react-native-firebase/auth');
              userId = authModule?.default?.()?.currentUser?.uid;
            } catch {
              /* ignore */
            }
          }

          if (userId) {
            if (Platform.OS === 'web') {
              // Web Firestore (firebase JS SDK)
              const { doc, getDoc, collection } = require('firebase/firestore');
              const { getWebDb } = require('./unifiedAuthService');

              const firestoreDb = getWebDb();
              if (firestoreDb && typeof firestoreDb === 'object') {
                const userDocRef = doc(firestoreDb, 'users', userId);
                const favoritesRef = collection(userDocRef, 'favorites');
                const favoritesDocRef = doc(favoritesRef, 'list');
                const favoritesDoc = await getDoc(favoritesDocRef);

                if (favoritesDoc.exists()) {
                  const data = favoritesDoc.data();
                  if (data && Array.isArray(data.favorites)) {
                    firestoreFavorites = data.favorites;
                    firestoreUpdatedAt = data.updatedAt || null;
                    console.log(`✅ FavoritesService: Loaded ${firestoreFavorites.length} favorites from Firestore (web)`);
                  } else {
                    console.warn('⚠️ FavoritesService: Firestore document exists but favorites field is invalid (web)');
                  }
                }
              }
            } else {
              // Mobile Firestore (@react-native-firebase/firestore)
              const { getMobileFirestore } = require('./unifiedAuthService');
              const mobileFirestoreGetter = getMobileFirestore();
              if (mobileFirestoreGetter) {
                const firestore = mobileFirestoreGetter();
                const favoritesDoc = await firestore
                  .collection('users')
                  .doc(userId)
                  .collection('favorites')
                  .doc('list')
                  .get();

                if (favoritesDoc.exists) {
                  const data = favoritesDoc.data();
                  if (data && Array.isArray(data.favorites)) {
                    firestoreFavorites = data.favorites;
                    firestoreUpdatedAt = data.updatedAt || null;
                    console.log(`✅ FavoritesService: Loaded ${firestoreFavorites.length} favorites from Firestore (mobile)`);
                  } else {
                    console.warn('⚠️ FavoritesService: Firestore document exists but favorites field is invalid (mobile)');
                  }
                }
              }
            }
          }
        }
      } catch (firestoreError: any) {
        // Keep noisy internal Firestore SDK errors out of logs
        if (!firestoreError?.message?.includes('Expected first argument to doc()')) {
          console.warn('⚠️ FavoritesService: Could not load favorites from Firestore:', firestoreError?.message || firestoreError);
        }
      }
      
      // MERGE STRATEGY: Combine local and Firestore data intelligently
      let finalFavorites: FavoriteBusiness[] = [];
      
      if (localFavorites.length > 0 && firestoreFavorites.length > 0) {
        // Both have data - merge them, preferring newer data
        const localIsNewer = localUpdatedAt && firestoreUpdatedAt 
          ? new Date(localUpdatedAt) > new Date(firestoreUpdatedAt)
          : localFavorites.length >= firestoreFavorites.length; // If no timestamps, prefer more items
        
        if (localIsNewer) {
          // Local is newer or has more items - use local as base, add any missing from Firestore
          const localIds = new Set(localFavorites.map(f => f.id));
          const missingFromLocal = firestoreFavorites.filter(f => !localIds.has(f.id));
          finalFavorites = [...localFavorites, ...missingFromLocal];
          console.log(`✅ FavoritesService: Merged favorites (local newer) - ${finalFavorites.length} total`);

          // Best-effort: push merged favorites back to Firestore for cross-device sync
          this.saveFavoritesToFirestore(finalFavorites).catch(() => {});
        } else {
          // Firestore is newer - use Firestore as base, add any missing from local
          const firestoreIds = new Set(firestoreFavorites.map(f => f.id));
          const missingFromFirestore = localFavorites.filter(f => !firestoreIds.has(f.id));
          finalFavorites = [...firestoreFavorites, ...missingFromFirestore];
          console.log(`✅ FavoritesService: Merged favorites (Firestore newer) - ${finalFavorites.length} total`);
          
          // Update local storage with merged data
          await SimpleStorage.setItem(this.FAVORITES_KEY, JSON.stringify(finalFavorites));
          if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
            try {
              window.localStorage.setItem(this.FAVORITES_KEY, JSON.stringify(finalFavorites));
            } catch (e) {
              console.warn('⚠️ FavoritesService: Could not update localStorage:', e);
            }
          }
        }
      } else if (localFavorites.length > 0) {
        // Only local has data - use it
        finalFavorites = localFavorites;
        console.log(`✅ FavoritesService: Using local favorites (${finalFavorites.length} items)`);

        // Best-effort: sync local-only favorites up to Firestore
        this.saveFavoritesToFirestore(finalFavorites).catch(() => {});
      } else if (firestoreFavorites.length > 0) {
        // Only Firestore has data - use it and sync to local
        finalFavorites = firestoreFavorites;
        console.log(`✅ FavoritesService: Using Firestore favorites (${finalFavorites.length} items), syncing to local`);
        
        // Sync to local storage
        await SimpleStorage.setItem(this.FAVORITES_KEY, JSON.stringify(finalFavorites));
        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
          try {
            window.localStorage.setItem(this.FAVORITES_KEY, JSON.stringify(finalFavorites));
          } catch (e) {
            console.warn('⚠️ FavoritesService: Could not sync to localStorage:', e);
          }
        }
      } else {
        // Neither has data - return empty array
        finalFavorites = [];
        console.log('ℹ️ FavoritesService: No favorites found in local or Firestore');
      }
      
      return finalFavorites;
    } catch (error) {
      console.error('Error getting favorites:', error);
      // On error, try to return local storage data as fallback
      try {
        if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
          const stored = window.localStorage.getItem(this.FAVORITES_KEY);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (Array.isArray(parsed)) {
              console.log(`✅ FavoritesService: Error fallback - returning ${parsed.length} favorites from localStorage`);
              return parsed;
            }
          }
        }
      } catch (fallbackError) {
        console.error('Error in fallback:', fallbackError);
      }
      return [];
    }
  }

  // Alias for getFavorites to match the interface expected by screens
  async getFavoriteBusinesses(): Promise<FavoriteBusiness[]> {
    return this.getFavorites();
  }

  async addToFavorites(business: Omit<FavoriteBusiness, 'addedDate'>): Promise<boolean> {
    try {
      const favorites = await this.getFavorites();
      
      // Check if already in favorites
      const existingIndex = favorites.findIndex(fav => fav.id === business.id);
      if (existingIndex !== -1) {
        return false; // Already in favorites
      }

      // Add to favorites
      const favoriteBusiness: FavoriteBusiness = {
        ...business,
        addedDate: new Date().toISOString(),
      };

      favorites.push(favoriteBusiness);
      await SimpleStorage.setItem(this.FAVORITES_KEY, JSON.stringify(favorites));
      
      // Also save to localStorage directly for immediate persistence
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem(this.FAVORITES_KEY, JSON.stringify(favorites));
        } catch (e) {
          console.warn('⚠️ FavoritesService: Could not save to localStorage:', e);
        }
      }
      
      // Sync to Firestore (async, don't wait)
      this.saveFavoritesToFirestore(favorites).catch(err => {
        console.warn('⚠️ FavoritesService: Could not save to Firestore:', err);
      });
      
      console.log(`Added ${business.name} to favorites`);
      return true;
    } catch (error) {
      console.error('Error adding to favorites:', error);
      return false;
    }
  }

  // Alias for addToFavorites to match the interface expected by screens
  async addFavorite(business: Omit<FavoriteBusiness, 'addedDate'>): Promise<boolean> {
    return this.addToFavorites(business);
  }

  async removeFromFavorites(businessId: string): Promise<boolean> {
    try {
      const favorites = await this.getFavorites();
      const updatedFavorites = favorites.filter(fav => fav.id !== businessId);
      
      if (updatedFavorites.length === favorites.length) {
        return false; // Business not found in favorites
      }

      await SimpleStorage.setItem(this.FAVORITES_KEY, JSON.stringify(updatedFavorites));
      
      // Also save to localStorage directly for immediate persistence
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem(this.FAVORITES_KEY, JSON.stringify(updatedFavorites));
        } catch (e) {
          console.warn('⚠️ FavoritesService: Could not save to localStorage:', e);
        }
      }
      
      // Sync to Firestore (async, don't wait)
      this.saveFavoritesToFirestore(updatedFavorites).catch(err => {
        console.warn('⚠️ FavoritesService: Could not save to Firestore:', err);
      });
      
      console.log(`Removed business ${businessId} from favorites`);
      return true;
    } catch (error) {
      console.error('Error removing from favorites:', error);
      return false;
    }
  }

  // Alias for removeFromFavorites to match the interface expected by screens
  async removeFavorite(businessId: string): Promise<boolean> {
    return this.removeFromFavorites(businessId);
  }

  async isFavorite(businessId: string): Promise<boolean> {
    try {
      const favorites = await this.getFavorites();
      return favorites.some(fav => fav.id === businessId);
    } catch (error) {
      console.error('Error checking if favorite:', error);
      return false;
    }
  }

  async toggleFavorite(business: Omit<FavoriteBusiness, 'addedDate'>): Promise<boolean> {
    try {
      const isCurrentlyFavorite = await this.isFavorite(business.id);
      
      if (isCurrentlyFavorite) {
        return await this.removeFromFavorites(business.id);
      } else {
        return await this.addToFavorites(business);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      return false;
    }
  }

  async clearFavorites(): Promise<boolean> {
    try {
      await SimpleStorage.setItem(this.FAVORITES_KEY, JSON.stringify([]));
      
      // Also clear from localStorage
      if (Platform.OS === 'web' && typeof window !== 'undefined' && window.localStorage) {
        try {
          window.localStorage.setItem(this.FAVORITES_KEY, JSON.stringify([]));
        } catch (e) {
          console.warn('⚠️ FavoritesService: Could not clear localStorage:', e);
        }
      }
      
      // Sync to Firestore (async, don't wait)
      this.saveFavoritesToFirestore([]).catch(err => {
        console.warn('⚠️ FavoritesService: Could not clear Firestore:', err);
      });
      
      console.log('Cleared all favorites');
      return true;
    } catch (error) {
      console.error('Error clearing favorites:', error);
      return false;
    }
  }

  // Save favorites to Firestore
  private async saveFavoritesToFirestore(favorites: FavoriteBusiness[]): Promise<void> {
    try {
      const { UnifiedAuthService, initializeFirebase } = require('./unifiedAuthService');

      const initialized = initializeFirebase();
      if (!initialized) return;

      const user = await UnifiedAuthService.getCurrentUser();
      let userId = user?.uid || user?.id;
      if (!userId && Platform.OS !== 'web') {
        try {
          const authModule = require('@react-native-firebase/auth');
          userId = authModule?.default?.()?.currentUser?.uid;
        } catch {
          /* ignore */
        }
      }
      if (!userId) return;

      const payload = { favorites, updatedAt: new Date().toISOString() };

      if (Platform.OS === 'web') {
        const { doc, setDoc, collection } = require('firebase/firestore');
        const { getWebDb } = require('./unifiedAuthService');
        const firestoreDb = getWebDb();
        if (!firestoreDb || typeof firestoreDb !== 'object') return;

        const userDocRef = doc(firestoreDb, 'users', userId);
        const favoritesRef = collection(userDocRef, 'favorites');
        const favoritesDocRef = doc(favoritesRef, 'list');
        await setDoc(favoritesDocRef, payload);
        console.log(`✅ FavoritesService: Saved ${favorites.length} favorites to Firestore (web)`);
      } else {
        const { getMobileFirestore } = require('./unifiedAuthService');
        const mobileFirestoreGetter = getMobileFirestore();
        if (!mobileFirestoreGetter) return;

        const firestore = mobileFirestoreGetter();
        await firestore
          .collection('users')
          .doc(userId)
          .collection('favorites')
          .doc('list')
          .set(payload, { merge: true });
        console.log(`✅ FavoritesService: Saved ${favorites.length} favorites to Firestore (mobile)`);
      }
    } catch (error: any) {
      if (!error.message?.includes('Expected first argument to doc()')) {
        console.error('Error saving favorites to Firestore:', error);
      }
      // Don't throw - this is a sync operation
    }
  }

  async getFavoritesCount(): Promise<number> {
    try {
      const favorites = await this.getFavorites();
      return favorites.length;
    } catch (error) {
      console.error('Error getting favorites count:', error);
      return 0;
    }
  }

  // Get favorites by category
  async getFavoritesByCategory(category: string): Promise<FavoriteBusiness[]> {
    try {
      const favorites = await this.getFavorites();
      return favorites.filter(fav => fav.category.toLowerCase().includes(category.toLowerCase()));
    } catch (error) {
      console.error('Error getting favorites by category:', error);
      return [];
    }
  }

  // Search favorites
  async searchFavorites(query: string): Promise<FavoriteBusiness[]> {
    try {
      const favorites = await this.getFavorites();
      const lowercaseQuery = query.toLowerCase();
      
      return favorites.filter(fav => 
        fav.name.toLowerCase().includes(lowercaseQuery) ||
        fav.category.toLowerCase().includes(lowercaseQuery) ||
        fav.services.some(service => service.toLowerCase().includes(lowercaseQuery))
      );
    } catch (error) {
      console.error('Error searching favorites:', error);
      return [];
    }
  }
}

export const favoritesService = new FavoritesService();
